<script setup>
import {computed, ref, reactive, onMounted, onBeforeUnmount, nextTick, watch} from "vue";
import { trans } from 'laravel-vue-i18n';
import Button from "primevue/button";
import InputNumber from "primevue/inputnumber";
import ConfirmDialog from "primevue/confirmdialog";
import Select from "primevue/select";
import FloatLabel from "primevue/floatlabel";
import {useConfirm} from "primevue/useconfirm";
import ToggleSwitch from "primevue/toggleswitch";
import WizardButton from "../../WizardButton.vue";

const model = defineModel();
const confirm = useConfirm();
const form = defineModel('form');

const props = defineProps({
    index: Number,
    day: String,
    minTime: Number,
    maxTime: Number,
    increment: Number,
    slotHeight: Number,
    selectedWorker: Number,
    formType: String,
})

const isDragging = ref(false);
const dragStartIndex = ref(null);
const dragCurrentIndex = ref(null);
const draggedSlot = reactive({time_start: null, time_end: null});

const draggingEvent = ref(null);
const draggingEventOriginal = reactive({start: null, end: null});
const draggingEventIndex = ref(null);
const draggingOffset = ref(0);
const touch = computed(() => matchMedia('(hover: none)').matches);


const safeModel = computed(() => model.value.filter(slot => slot.worker === props.selectedWorker))

const resizeInfo = reactive({
    isResizing: false,
    event: null,
    direction: null,
    originalEvent: null
});

const slotContainer = ref(null);

const daySlots = computed(() => {
    let slots = [];
    for (let time = props.minTime; time <= props.maxTime; time += props.increment) {
        slots.push({
            time_start: numberToTime(time),
            time_end: numberToTime(time + props.increment),
            has_border: time % 1 !== 0
        });
    }
    return slots;
})

const numberToTime = (num) => {
    const hours = Math.floor(num)
    const minutes = (num - hours) * 60
    return `${hours.toString().padStart(2, '0')}:${Math.round(minutes).toString().padStart(2, '0')}`
}

const timeToNumber = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h + m / 60;
}

function slotsOverlap(slotA, slotB) {
    const startA = timeToNumber(slotA.start);
    const endA = timeToNumber(slotA.end);
    const startB = timeToNumber(slotB.start);
    const endB = timeToNumber(slotB.end);
    return startA < endB && endA > startB;
}

const computedHeight = computed(() => {
    return ((props.increment % 1 === 0) ? props.slotHeight : props.increment * props.slotHeight);
})

const getStyle = (event) => {
    const eventStart = timeToNumber(event.start);
    const eventEnd = timeToNumber(event.end);

    const topSlots = (eventStart - props.minTime) / props.increment;
    const durationSlots = (eventEnd - eventStart) / props.increment;

    return {
        top: `${topSlots * computedHeight.value}px`,
        height: durationSlots * computedHeight.value ,
    };
}

const getHeight = (event) => {
    const eventEnd = timeToNumber(event.end);
    const eventStart = timeToNumber(event.start);
    const size = (eventEnd - eventStart) / props.increment;
    return {
        bottom: (size === 1 ? -8 : (size === 2 ? 2 : 8)) + 'px'
    }
}

const updateDraggedSlot = () => {
    if (dragStartIndex.value === null || dragCurrentIndex.value === null) return;
    const startIndex = Math.min(dragStartIndex.value, dragCurrentIndex.value);
    const endIndex = Math.max(dragStartIndex.value, dragCurrentIndex.value) + 1;
    draggedSlot.time_start = daySlots.value[startIndex].time_start;
    draggedSlot.time_end = daySlots.value[endIndex] ? daySlots.value[endIndex].time_start : numberToTime(props.maxTime);
}

const getSlotIndexFromMouse = (event) => {
    if (!slotContainer.value) return null;
    const rect = slotContainer.value.getBoundingClientRect();
    const clientY = event.touches && event.touches[0] ? event.touches[0].clientY : event.clientY;
    const y = clientY - rect.top;
    let index = Math.floor(y / computedHeight.value);
    if (index < 0) index = 0;
    if (index >= daySlots.value.length) index = daySlots.value.length - 1;
    return index;
}

const onMouseDown = (event) => {
    if (activeEvent.value) return;
    if (resizeInfo.isResizing) return;

    const eventTarget = event.target.closest('.dp-event');
    if (eventTarget) {
        // Start dragging existing event
        const eventKey = eventTarget.getAttribute('data-key');
        let foundIndex = null;
        for (let i = 0; i < model.value.length; i++) {
            const e = model.value[i];
            if ((e.id && e.id.toString() === eventKey) || (e.start + e.end + e.worker === eventKey)) {
                foundIndex = i;
                break;
            }
        }
        if (foundIndex !== null) {
            draggingEventIndex.value = foundIndex;
            draggingEvent.value = model.value[foundIndex];
            draggingEventOriginal.start = draggingEvent.value.start;
            draggingEventOriginal.end = draggingEvent.value.end;
            isDragging.value = false;
            dragStartIndex.value = null;
            dragCurrentIndex.value = null;
            draggedSlot.time_start = null;
            draggedSlot.time_end = null;

            const eventRect = eventTarget.getBoundingClientRect();
            const clientY = event.touches ? event.touches[0].clientY : event.clientY;
            const offsetY = clientY - eventRect.top;
            const slotHeight = computedHeight.value;
            const offsetSlots = offsetY / slotHeight;
            draggingOffset.value = offsetSlots * props.increment * 60;

            window.addEventListener('pointermove', onMouseMove);
            window.addEventListener('pointerup', onMouseUp);
            event.preventDefault();
            return;
        }
    }

    if (!eventTarget) {
        isDragging.value = true;
        dragStartIndex.value = getSlotIndexFromMouse(event);
        dragCurrentIndex.value = dragStartIndex.value;
        updateDraggedSlot();
        draggingEvent.value = null;
        draggingEventIndex.value = null;
        draggingOffset.value = 0;
        window.addEventListener('pointermove', onMouseMove);
        window.addEventListener('pointerup', onMouseUp);
        event.preventDefault();
    }
}

const onMouseMove = (event) => {
    if (event.touches) event.preventDefault();
    if (isDragging.value) {
        const newIndex = getSlotIndexFromMouse(event);
        if (newIndex === null) return;
        const startIndex = Math.min(dragStartIndex.value, newIndex);
        const endIndex = Math.max(dragStartIndex.value, newIndex) + 1;
        const slotStart = daySlots.value[startIndex].time_start;
        const slotEnd = daySlots.value[endIndex]
            ? daySlots.value[endIndex].time_start
            : numberToTime(props.maxTime);
        const newSlot = {start: slotStart, end: slotEnd};
        const collision = safeModel.value.some(slot => slotsOverlap(newSlot, slot));
        if (!collision) {
            dragCurrentIndex.value = newIndex;
            updateDraggedSlot();
        }
    } else if (draggingEvent.value) {
        if (!slotContainer.value) return;
        const slotIndex = getSlotIndexFromMouse(event);
        if (slotIndex === null) return;
        const offsetSlots = draggingOffset.value / (props.increment * 60);
        let newStartIndex = Math.round(slotIndex - offsetSlots);
        if (newStartIndex < 0) newStartIndex = 0;
        const durationSlots = Math.round((timeToNumber(draggingEventOriginal.end) - timeToNumber(draggingEventOriginal.start)) / props.increment);
        if (newStartIndex + durationSlots > daySlots.value.length) {
            newStartIndex = daySlots.value.length - durationSlots;
            if (newStartIndex < 0) newStartIndex = 0;
        }
        const newStartNum = props.minTime + (newStartIndex * props.increment);
        const clampedStartNum = Math.max(props.minTime, Math.min(newStartNum, props.maxTime - durationSlots * props.increment));
        const clampedEndNum = Math.min(props.maxTime, clampedStartNum + durationSlots * props.increment);
        const testSlot = {
            start: numberToTime(clampedStartNum),
            end: numberToTime(clampedEndNum),
            id: draggingEvent.value.id
        };
        const collision = model.value.some((slot, idx) => {
            if (slot.worker === props.selectedWorker) {
                if (idx === draggingEventIndex.value) return false;
                return slotsOverlap(testSlot, slot);
            }
            return false;
        });
        if (!collision) {
            draggingEvent.value.start = testSlot.start;
            draggingEvent.value.end = testSlot.end;
        }
    } else if (resizeInfo.isResizing) {
        onResizeMove(event);
    }
}

const onMouseUp = (event) => {
    if (isDragging.value) {
        isDragging.value = false;
        // Ensure clientY is set for touchend events
        if (event.changedTouches && event.changedTouches.length > 0) {
            event.clientY = event.changedTouches[0].clientY;
        }
        dragCurrentIndex.value = getSlotIndexFromMouse(event);
        updateDraggedSlot();
        if (draggedSlot.time_start && draggedSlot.time_end && draggedSlot.time_start !== draggedSlot.time_end) {
            // Check collision before adding
            const newSlot = {
                start: draggedSlot.time_start,
                end: draggedSlot.time_end,
                type: safeModel.value[0]?.type ?? 'work',
                interval: 30,
                pause: 30,
                service_length_override: true,
                worker: props.selectedWorker
            };
            const collision = safeModel.value.some(slot => slotsOverlap(newSlot, slot));
            if (!collision) {
                model.value.push(newSlot);
            }
        }
        dragStartIndex.value = null;
        dragCurrentIndex.value = null;
        draggedSlot.time_start = null;
        draggedSlot.time_end = null;
        draggingOffset.value = 0;
        window.removeEventListener('pointermove', onMouseMove);
        window.removeEventListener('pointerup', onMouseUp);
    } else if (draggingEvent.value) {
        // Update the existing slot in model.value
        if (draggingEventIndex.value !== null) {
            model.value.splice(draggingEventIndex.value, 1, {...draggingEvent.value});
        }
        draggingEvent.value = null;
        draggingEventIndex.value = null;
        draggingOffset.value = 0;
        window.removeEventListener('pointermove', onMouseMove);
        window.removeEventListener('pointerup', onMouseUp);
    }
    if (resizeInfo.isResizing) {
        onResizeEnd();
    }
}

const onResizeStart = (event, ev, direction) => {
    resizeInfo.isResizing = true;
    resizeInfo.event = ev;
    resizeInfo.direction = direction;
    resizeInfo.originalEvent = event;
    resizeInfo.originalStart = timeToNumber(ev.start);
    resizeInfo.originalEnd = timeToNumber(ev.end);
    resizeInfo.evRef = ev;
    window.addEventListener('pointermove', onMouseMove);
    window.addEventListener('pointerup', onMouseUp);
    event.stopPropagation();
    event.preventDefault();
}

const onResizeMove = (event) => {
    if (!resizeInfo.isResizing) return;
    if (!slotContainer.value) return;
    const rect = slotContainer.value.getBoundingClientRect();
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;
    const y = clientY - rect.top;
    let index = Math.floor(y / computedHeight.value);
    if (index < 0) index = 0;
    if (index >= daySlots.value.length) index = daySlots.value.length - 1;
    const slotTime = timeToNumber(daySlots.value[index].time_start);
    let testSlot;
    if (resizeInfo.direction === 'start') {
        const newStart = Math.min(slotTime, resizeInfo.originalEnd - props.increment);
        testSlot = {
            start: numberToTime(newStart),
            end: resizeInfo.evRef.end,
            id: resizeInfo.evRef.id
        };
        const collision = safeModel.value.some((slot) => {
            if ((slot.id && resizeInfo.evRef.id && slot.id === resizeInfo.evRef.id) ||
                (slot.start === resizeInfo.evRef.start && slot.end === resizeInfo.evRef.end)) return false;
            return slotsOverlap(testSlot, slot);
        });
        if (!collision) {
            resizeInfo.evRef.start = numberToTime(newStart);
        }
    } else if (resizeInfo.direction === 'end') {
        const newEnd = Math.max(slotTime + props.increment, resizeInfo.originalStart + props.increment);
        testSlot = {
            start: resizeInfo.evRef.start,
            end: numberToTime(newEnd),
            id: resizeInfo.evRef.id
        };
        const collision = safeModel.value.some((slot) => {
            if ((slot.id && resizeInfo.evRef.id && slot.id === resizeInfo.evRef.id) ||
                (slot.start === resizeInfo.evRef.start && slot.end === resizeInfo.evRef.end)) return false;
            return slotsOverlap(testSlot, slot);
        });
        if (!collision) {
            resizeInfo.evRef.end = numberToTime(newEnd);
        }
    }
}

const onResizeEnd = () => {
    if (!resizeInfo.isResizing) return;
    resizeInfo.isResizing = false;
    const ev = resizeInfo.evRef;
    if (ev) {
        const idx = model.value.findIndex(e => (e.id && e.id === ev.id) || (e.start === ev.start && e.end === ev.end));
        if (idx !== -1) {
            model.value.splice(idx, 1, {...ev});
        }
    }
    resizeInfo.event = null;
    resizeInfo.direction = null;
    resizeInfo.originalEvent = null;
    resizeInfo.evRef = null;
    window.removeEventListener('pointermove', onMouseMove);
    window.removeEventListener('pointerup', onMouseUp);
}
const activeEvent = defineModel('activeEvent');

function openEventModal(event) {

    window.removeEventListener('pointermove', onMouseMove);
    window.removeEventListener('pointerup', onMouseUp);
    activeEvent.value = (event.id || (event.start + event.end + event.worker)) + props.day;
}

function closeModal() {
    activeEvent.value = null;
}

onMounted(() => {
    window.addEventListener('click', closeModal);
});

onBeforeUnmount(() => {
    window.removeEventListener('click', closeModal);
});

const getBlocks = (event) => {
    const totalHeight = Math.round(parseFloat(getStyle(event).height));
    const interval = Number(event.interval);
    const pause = Number(event.pause);
    const totalMinutes = (timeToNumber(event.end) - timeToNumber(event.start)) * 60;
    const blocks = [];

    const totalBlocks = Math.floor((totalMinutes + pause) / (interval + pause));

    // px na minutu
    const pxPerMinute = (totalHeight- 2) / totalMinutes;

    for (let i = 0; i < totalBlocks; i++) {
        const start = numberToTime(
            timeToNumber(event.start) + i * ((interval + pause) / 60)
        );
        const end = numberToTime(
            timeToNumber(event.start) + i * ((interval + pause) / 60) + (interval / 60)
        );

        const blockHeight = interval * pxPerMinute;
        const pauseHeight = pause * pxPerMinute;

        blocks.push({
            type: 'block',
            start,
            end,
            height: blockHeight,
            interval
        });

        // trailing pause jen pokud není poslední blok
        if (i !== totalBlocks - 1 && pause > 0) {
            blocks.push({
                type: 'pause',
                height: pauseHeight,
                interval
            });
        }
    }

    return blocks;
}

function setAllSlotsType(type) {
    if (type === 'work') {
        if (safeModel.value.length > 1) {
            confirm.require({
                group: 'mass-setter',
                message: trans('forms.timeslot_confirm_switch_message'),
                header: trans('forms.timeslot_confirm_switch_header'),
                icon: 'pi pi-info-circle',
                position: 'bottom',
                rejectProps: {
                    label: trans('forms.timeslot_confirm_stay'),
                    severity: 'secondary',
                    text: true
                },
                acceptProps: {
                    label: trans('forms.timeslot_confirm_switch'),
                    text: true
                },
                accept: () => {
                    const starts = safeModel.value.map(s => timeStringToMinutes(s.start));
                    const ends = safeModel.value.map(s => timeStringToMinutes(s.end));
                    const earliestStart = Math.min(...starts);
                    const latestEnd = Math.max(...ends);
                    let safeIndex = false;

                    for (let i = model.value.length - 1; i >= 0; i--) {
                        const slot = model.value[i];
                        if (slot.worker === props.selectedWorker) {
                            if (safeIndex === false) {
                                safeIndex = i;
                            } else {
                                model.value.splice(i, 1);
                            }
                        }
                    }

                    safeModel.value[0].start = minutesToTimeString(earliestStart);
                    safeModel.value[0].end = minutesToTimeString(latestEnd);
                    model.value.forEach(slot => {
                        if (slot.worker === props.selectedWorker) {
                            slot.type = type;
                        }
                    });
                },
                reject: () => {
                    setTimeout(() =>
                        model.value.forEach(slot => {
                            if (slot.worker === props.selectedWorker) {
                                slot.type = 'standard';
                            }
                        })
                        , 600);
                }
            });
        } else {
            model.value.forEach(slot => {
                if (slot.worker === props.selectedWorker) {
                    slot.type = type;
                }
            });
        }
    } else {
        model.value.forEach(slot => {
            if (slot.worker === props.selectedWorker) {
                slot.type = type;
            }
        });
    }
}

function timeStringToMinutes(time) {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
}

function minutesToTimeString(mins) {
    const h = String(Math.floor(mins / 60)).padStart(2, '0');
    const m = String(mins % 60).padStart(2, '0');
    return `${h}:${m}`;
}

const typeOptions = [
    { label: trans('forms.slot_type_standard'), value: 'standard' },
  //  { label: trans('forms.slot_type_free'), value: 'free' },
    { label: trans('forms.slot_type_work'), value: 'work' }
];

const selectedType = computed({
    get: () => safeModel.value[0]?.type || 'standard',
    set: (val) => setAllSlotsType(val)
});
</script>

<template>
    <div class="day-part">
        <div class="day-part-header">
            <slot name="pre"><div></div></slot>
            {{ day }}
            <slot name="post"><div></div></slot>
        </div>
        <div class="relative"
             ref="slotContainer"
             @mousedown="onMouseDown"
             @mousemove="onMouseMove"
             @mouseup="onMouseUp"
             @touchstart="onMouseDown"
             @touchend="onMouseUp"
             @pointerdown="onMouseDown">
            <div v-for="slot in daySlots" :key="slot.time_start" class="day-part-time-slot"
                 :class="{ 'border-b border-solid':slot.has_border }" :data-start="slot.time_start" :data-end="slot.time_end"
                 ref="slotsEl"
                 :style="{
                height: `${computedHeight}px`,
            }"></div>

            <div v-if="isDragging" class="dp-event dragged-slot" :style="{
                top: `${Math.min(dragStartIndex, dragCurrentIndex) * computedHeight}px`,
                height: `${(Math.abs(dragCurrentIndex - dragStartIndex) + 1) * computedHeight - 6}px`,
            }">
                <p class="text-sm">{{ draggedSlot.time_start }} - {{ draggedSlot.time_end }}</p>


            </div>

            <template v-for="(event, index) in model"
                      :key="event.id || (event.start + event.end + event.worker)">
            <div
                v-if="selectedWorker === event.worker"
                 class="dp-event"
                 :class="[{ 'dp-event-active': activeEvent === (event.id || (event.start + event.end + event.worker))+day }, `event-${event.type}`, event.service_length_override ? '' : '!border-b-8 !border-b-red-500']"
                 :data-key="event.id || (event.start + event.end + event.worker)"
                 :style="{
                     top: getStyle(event).top,
                     height: `${getStyle(event).height + 8}px`,
                 }"
                 title="Drag to move, use handles to resize"
                 @click.stop.prevent="openEventModal(event)"
                    @tap="openEventModal(event)">
                <div v-if="touch" class="absolute right-2" :style="getHeight(event)">
                    <Button icon="pi pi-cog" severity="secondary" @pointerdown.stop="openEventModal(event)" rounded size="small"></Button>
                </div>
                <div class="resize-handle resize-top relative"
                     @mousedown.stop.prevent="onResizeStart($event, event, 'start')"
                     @pointerdown.stop.prevent="onResizeStart($event, event, 'start')"
                     @touchstart.stop.prevent="onResizeStart($event, event, 'start')">

                    <div class="w-full md:hidden flex justify-start top-0 absolute z-50">
                        <div class="w-[24px]"></div>
                        <i class="pi pi-bars bg-neutral-50 border rounded relative z-50 px-8 !text-sm dark:bg-neutral-900 dark:border-neutral-700"></i>
                    </div>
                </div>

                <div class="work-repeater overflow-hidden" v-if="event.type === 'work'">
                    <template v-for="(block, i) in getBlocks(event)"
                              :key="i">
                        <div
                            v-if="block.type === 'block'"
                            class="work-block"
                            :style="{ height: `${block.height}px` }"
                        >
                            {{ block.start }} - {{ block.end }}
                        </div>
                        <div
                            v-if="block.type === 'pause'"
                            class="pause-block"
                            :style="{ height: `${block.height}px` }"
                        >
                        </div>
                    </template>
                </div>


                <p v-if="event.type !== 'work'" class="text-sm text-center">{{ event.start }} - {{ event.end }}</p>

                <div class="resize-handle resize-bottom relative"
                     @mousedown.stop.prevent="onResizeStart($event, event, 'end')"
                     @pointerdown.stop.prevent="onResizeStart($event, event, 'end')"
                     @touchstart.stop.prevent="onResizeStart($event, event, 'end')">
                    <div class="w-full md:hidden absolute bottom-0 flex justify-end z-50">
                        <i class="pi pi-bars bg-neutral-50 border rounded relative z-50 px-8 !text-sm dark:bg-neutral-900 dark:border-neutral-700"></i>
                        <div class="w-[24px]"></div>
                    </div>
                </div>
                <transition name="slide-fwd" mode="out-in">
                    <div
                        v-if="activeEvent === (event.id || (event.start + event.end + event.worker)) + day"
                        class="dp-event-modal"
                    >
                        <div class="relative flex flex-col w-full">
                            <!-- Header -->
                            <div class="flex justify-between gap-2 items-center">
                                <p class="border rounded px-2 py-1 bg-neutral-50 grow text-center whitespace-nowrap">
                                    {{ event.start }} - {{ event.end }}
                                </p>
                                <Button
                                    severity="danger"
                                    variant="text"
                                    size="small"
                                    icon="pi pi-trash"
                                    @click="model.splice(index, 1)"
                                    rounded
                                />
                            </div>

                            <!-- Work settings -->
                            <div v-if="event.type === 'work'" class="flex flex-col gap-4 mt-4">
                                <FloatLabel variant="on">
                                    <InputNumber id="interval" fluid v-model="event.interval" :min="1" />
                                    <label for="interval">{{ $t('forms.slot_interval_label') }}</label>
                                </FloatLabel>
                                <FloatLabel variant="on">
                                    <InputNumber id="pause" fluid v-model="event.pause" :min="0" />
                                    <label for="pause">{{ $t('forms.slot_pause_label') }}</label>
                                </FloatLabel>
                            </div>
                            <template v-if="formType === 'service'">
                                <div class="flex items-center mt-2 rounded bg-neutral-50 border p-2 pointer-events-auto">
                                    <p class="text-sm">{{ event.type === 'work' ? $t('forms.slot_allow_overflow_with_last') : $t('forms.slot_allow_overflow_no_last') }}</p>
                                    <div>
                                        <ToggleSwitch :model-value="event.service_length_override"  @click="event.service_length_override = !event.service_length_override"/>
                                    </div>
                                </div>
                                <span class="text-sm text-center mt-3 opacity-50">
                                        {{ $t('forms.slot_duration_note') }}
                                    <template v-if="!event.service_length_override && event.type === 'work'">
                                        {{ $t('forms.slot_overflow_disabled_work') }}
                                    </template>
                                    <template v-else-if="!event.service_length_override && event.type !== 'work'">
                                        {{ $t('forms.slot_overflow_disabled_standard') }}
                                    </template>
                                </span>
                            </template>
                        </div>
                    </div>
                </transition>
            </div>
            </template>
        </div>
        <div class="p-2 selector-of-type flex items-center gap-1">
            <Select
                :id="`selector-${index}`"
                v-model="selectedType"
                size="small"
                fluid
                class="!min-w-0"
                :options="typeOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="!safeModel.length"
            >
                <template #value="slotProps">
                    <p class="h-4 w-6 overflow-hidden">
                        <span class="absolute left-2 text-ellipsis right-0">{{typeOptions.find(el => el.value === slotProps.value)?.label}}</span>
                    </p>
                </template>
            </Select>
            <i class="pi pi-info-circle opacity-50 shrink-0" v-tooltip.top="$t('forms.slot_type_help')"></i>
        </div>

    </div>
</template>

<style scoped lang="scss">
@use '../../../../../../../../resources/css/calendarSelector';
</style>
